import {
    TopologyLink,
    TopologySite,
    PathHop,
    LatencyPath,
    SiteLatencyPath,
    SiteLatencyResult,
    SptResult,
    SptNode,
    parseCarrier,
    resolveLinkBandwidth,
} from '../TopologyData';

interface AdjEdge {
    neighbor: string;
    linkId: string;
    forwardUs: number;
    reverseUs: number;
    bandwidth: string;
    bandwidthKbps: number;
    carrier: string | null;
    lossPercent: number | null;
}

type AdjacencyList = Map<string, AdjEdge[]>;

/**
 * Build a bidirectional adjacency list from topology links.
 * For each link, two edges are created:
 *   source->target: forward = link.forward, reverse = link.reverse
 *   target->source: forward = link.reverse, reverse = link.forward (swapped)
 */
function buildAdjacencyList(links: TopologyLink[]): AdjacencyList {
    const adj: AdjacencyList = new Map();

    for (const link of links) {
        const fwd = link.forward?.avgDelayUs ?? link.avgDelayUs;
        const rev = link.reverse?.avgDelayUs ?? link.avgDelayUs;
        const carrier =
            parseCarrier(link.sourceInterfaceDesc) ??
            parseCarrier(link.targetInterfaceDesc);
        const bw = resolveLinkBandwidth(link);

        // source -> target
        if (!adj.has(link.source)) adj.set(link.source, []);
        const srcEdges = adj.get(link.source);
        const fwdLoss = link.forward?.lossPercent ?? null;
        const revLoss = link.reverse?.lossPercent ?? null;

        if (srcEdges) {
            srcEdges.push({
                neighbor: link.target,
                linkId: link.id,
                forwardUs: fwd,
                reverseUs: rev,
                bandwidth: bw.label,
                bandwidthKbps: bw.kbps,
                carrier,
                lossPercent: fwdLoss,
            });
        }

        // target -> source (swap forward/reverse)
        if (!adj.has(link.target)) adj.set(link.target, []);
        const tgtEdges = adj.get(link.target);
        if (tgtEdges) {
            tgtEdges.push({
                neighbor: link.source,
                linkId: link.id,
                forwardUs: rev,
                reverseUs: fwd,
                bandwidth: bw.label,
                bandwidthKbps: bw.kbps,
                carrier,
                lossPercent: revLoss,
            });
        }
    }

    return adj;
}

interface RawPath {
    nodes: string[];
    edges: AdjEdge[];
}

/**
 * DFS to find all simple paths (no repeated nodes) from src to dst.
 * maxDepth limits the number of hops to prevent combinatorial explosion.
 */
function findAllPaths(
    adj: AdjacencyList,
    src: string,
    dst: string,
    maxDepth = 8
): RawPath[] {
    const results: RawPath[] = [];
    const visited = new Set<string>();

    function dfs(current: string, path: string[], edges: AdjEdge[]): void {
        if (current === dst) {
            results.push({ nodes: [...path], edges: [...edges] });
            return;
        }
        if (edges.length >= maxDepth) return;

        const neighbors = adj.get(current);
        if (!neighbors) return;

        for (const edge of neighbors) {
            if (visited.has(edge.neighbor)) continue;
            visited.add(edge.neighbor);
            path.push(edge.neighbor);
            edges.push(edge);
            dfs(edge.neighbor, path, edges);
            edges.pop();
            path.pop();
            visited.delete(edge.neighbor);
        }
    }

    visited.add(src);
    dfs(src, [src], []);

    return results;
}

/**
 * Compute all latency paths between two nodes, sorted by RTT ascending.
 * Returns an empty array if no paths exist or source === target.
 */
export function computeLatencyPaths(
    links: TopologyLink[],
    source: string,
    target: string
): LatencyPath[] {
    if (source === target || !source || !target) return [];

    const adj = buildAdjacencyList(links);
    const rawPaths = findAllPaths(adj, source, target);

    const paths: LatencyPath[] = rawPaths.map((raw) => {
        const hops: PathHop[] = raw.edges.map((edge, i) => ({
            from: raw.nodes[i],
            to: edge.neighbor,
            linkId: edge.linkId,
            forwardLatencyUs: edge.forwardUs,
            reverseLatencyUs: edge.reverseUs,
            rttUs: edge.forwardUs + edge.reverseUs,
            bandwidth: edge.bandwidth,
            bandwidthKbps: edge.bandwidthKbps,
            carrier: edge.carrier,
            lossPercent: edge.lossPercent,
        }));

        const forwardLatencyUs = hops.reduce(
            (sum, h) => sum + h.forwardLatencyUs,
            0
        );
        const reverseLatencyUs = hops.reduce(
            (sum, h) => sum + h.reverseLatencyUs,
            0
        );

        // Bottleneck = minimum bandwidth across all hops
        const minBwHop = hops.reduce((min, h) =>
            h.bandwidthKbps < min.bandwidthKbps ? h : min
        );

        return {
            hops,
            forwardLatencyUs,
            reverseLatencyUs,
            rttUs: forwardLatencyUs + reverseLatencyUs,
            hopCount: hops.length,
            isBest: false,
            isDirect: hops.length === 1,
            bottleneckBandwidth: minBwHop.bandwidth,
            bottleneckBandwidthKbps: minBwHop.bandwidthKbps,
        };
    });

    // Sort by RTT ascending
    paths.sort((a, b) => a.rttUs - b.rttUs);

    // Mark the best path
    if (paths.length > 0) {
        paths[0].isBest = true;
    }

    return paths;
}

/**
 * Compute all latency paths between two sites (datacenters).
 * Finds paths between every combination of routers in sourceSite → targetSite,
 * aggregates stats, and returns a unified sorted result.
 */
export function computeSiteLatencyPaths(
    links: TopologyLink[],
    sites: TopologySite[],
    sourceSite: string,
    targetSite: string
): SiteLatencyResult | null {
    if (sourceSite === targetSite || !sourceSite || !targetSite) return null;

    const srcSite = sites.find((s) => s.id === sourceSite);
    const tgtSite = sites.find((s) => s.id === targetSite);
    if (!srcSite || !tgtSite) return null;

    const srcNodeIds = srcSite.nodes.map((n) => n.id);
    const tgtNodeIds = tgtSite.nodes.map((n) => n.id);

    // Build adjacency once, reuse for all pairs
    const adj = buildAdjacencyList(links);
    const allPaths: SiteLatencyPath[] = [];

    for (const srcId of srcNodeIds) {
        for (const tgtId of tgtNodeIds) {
            const rawPaths = findAllPaths(adj, srcId, tgtId);

            for (const raw of rawPaths) {
                const hops: PathHop[] = raw.edges.map((edge, i) => ({
                    from: raw.nodes[i],
                    to: edge.neighbor,
                    linkId: edge.linkId,
                    forwardLatencyUs: edge.forwardUs,
                    reverseLatencyUs: edge.reverseUs,
                    rttUs: edge.forwardUs + edge.reverseUs,
                    bandwidth: edge.bandwidth,
                    bandwidthKbps: edge.bandwidthKbps,
                    carrier: edge.carrier,
                    lossPercent: edge.lossPercent,
                }));

                const forwardLatencyUs = hops.reduce(
                    (sum, h) => sum + h.forwardLatencyUs,
                    0
                );
                const reverseLatencyUs = hops.reduce(
                    (sum, h) => sum + h.reverseLatencyUs,
                    0
                );

                const minBwHop = hops.reduce((min, h) =>
                    h.bandwidthKbps < min.bandwidthKbps ? h : min
                );

                allPaths.push({
                    sourceRouter: srcId,
                    targetRouter: tgtId,
                    hops,
                    forwardLatencyUs,
                    reverseLatencyUs,
                    rttUs: forwardLatencyUs + reverseLatencyUs,
                    hopCount: hops.length,
                    isBest: false,
                    isDirect: hops.length === 1,
                    bottleneckBandwidth: minBwHop.bandwidth,
                    bottleneckBandwidthKbps: minBwHop.bandwidthKbps,
                });
            }
        }
    }

    // Sort by RTT ascending
    allPaths.sort((a, b) => a.rttUs - b.rttUs);

    if (allPaths.length > 0) {
        allPaths[0].isBest = true;
    }

    const rttValues = allPaths.map((p) => p.rttUs);
    const bestRttUs = rttValues.length > 0 ? rttValues[0] : 0;
    const worstRttUs =
        rttValues.length > 0 ? rttValues[rttValues.length - 1] : 0;
    const avgRttUs =
        rttValues.length > 0
            ? Math.round(
                  rttValues.reduce((a, b) => a + b, 0) / rttValues.length
              )
            : 0;

    return {
        sourceSite,
        targetSite,
        sourceLocation: srcSite.location,
        targetLocation: tgtSite.location,
        totalPaths: allPaths.length,
        bestRttUs,
        worstRttUs,
        avgRttUs,
        paths: allPaths,
    };
}

/**
 * Cost function for one IS-IS link. The default uses `link.metric`
 * (standard IGP). Pass a custom function — e.g. one that returns
 * `link.avgDelayUs` — to compute SPF for a flex-algo whose
 * `metricType` is min-delay (1) or TE (2).
 */
export type LinkMetricFn = (link: TopologyLink) => number;

/** Default IS-IS metric: just the wide metric on the link. */
const defaultMetricFn: LinkMetricFn = (link) => link.metric;

/**
 * Build a cost function for the given flex-algo `metricType`.
 *  - 0 (igp)        → `link.metric`
 *  - 1 (min-delay)  → `link.avgDelayUs` (microseconds, so larger spread)
 *  - 2 (te)         → not exposed in topology yet; falls back to `link.metric`.
 *                      `parse-isis-lsdb.js → parseAdjacency` would have to
 *                      surface `teMetric` per link to make this exact.
 */
export function metricFnForFlexAlgo(metricType: 0 | 1 | 2): LinkMetricFn {
    if (metricType === 1) return (l) => l.avgDelayUs ?? l.metric;
    return defaultMetricFn;
}

/**
 * Compute the IS-IS Shortest Path Tree (SPT) from a root node using
 * Dijkstra's algorithm. Cost defaults to `link.metric` (standard IGP)
 * but can be overridden via `metricFn` to compute a flex-algo SPT
 * (e.g. delay-based for `metricType: 1`).
 *
 * Optionally excludes a set of link IDs or node IDs to simulate failures.
 */
export function computeIsisSpt(
    links: TopologyLink[],
    root: string,
    excludedLinks?: Set<string>,
    excludedNodes?: Set<string>,
    metricFn: LinkMetricFn = defaultMetricFn
): SptResult {
    const adj = new Map<string, { neighbor: string; linkId: string; metric: number }[]>();

    for (const link of links) {
        if (excludedLinks?.has(link.id)) continue;
        if (excludedNodes?.has(link.source) || excludedNodes?.has(link.target)) continue;

        const cost = metricFn(link);

        if (!adj.has(link.source)) adj.set(link.source, []);
        const srcList = adj.get(link.source);
        if (srcList) {
            srcList.push({ neighbor: link.target, linkId: link.id, metric: cost });
        }

        if (!adj.has(link.target)) adj.set(link.target, []);
        const tgtList = adj.get(link.target);
        if (tgtList) {
            tgtList.push({ neighbor: link.source, linkId: link.id, metric: cost });
        }
    }

    const nodes: Record<string, SptNode> = {};
    const visited = new Set<string>();

    // Min-heap via simple array (network sizes are small enough)
    const queue: { id: string; cost: number }[] = [];

    nodes[root] = { id: root, cost: 0, parent: null, parentLinkId: null };
    queue.push({ id: root, cost: 0 });

    while (queue.length > 0) {
        queue.sort((a, b) => a.cost - b.cost);
        const current = queue.shift();
        if (!current) break;
        if (visited.has(current.id)) continue;
        visited.add(current.id);

        const neighbors = adj.get(current.id);
        if (!neighbors) continue;

        for (const edge of neighbors) {
            if (visited.has(edge.neighbor)) continue;
            const newCost = current.cost + edge.metric;
            const existing = nodes[edge.neighbor];
            if (!existing || newCost < existing.cost) {
                nodes[edge.neighbor] = {
                    id: edge.neighbor,
                    cost: newCost,
                    parent: current.id,
                    parentLinkId: edge.linkId,
                };
                queue.push({ id: edge.neighbor, cost: newCost });
            }
        }
    }

    const treeEdgeIds = new Set<string>();
    for (const node of Object.values(nodes)) {
        if (node.parentLinkId) treeEdgeIds.add(node.parentLinkId);
    }

    return { root, nodes, treeEdgeIds };
}

/**
 * Extract the specific shortest path from source to target out of an SPT
 * result. Returns the set of link IDs and node IDs on the path, or null
 * if the target is unreachable.
 */
export function extractP2pPath(
    spt: SptResult,
    target: string
): { edgeIds: Set<string>; nodeIds: Set<string> } | null {
    const node = spt.nodes[target];
    if (!node) return null;

    const edgeIds = new Set<string>();
    const nodeIds = new Set<string>();
    let current: SptNode | undefined = node;

    while (current) {
        nodeIds.add(current.id);
        if (current.parentLinkId) edgeIds.add(current.parentLinkId);
        current = current.parent ? spt.nodes[current.parent] : undefined;
    }

    return { edgeIds, nodeIds };
}
